import { ActionIcon, Button, Select, TextInput } from '@mantine/core';
import { IconCalendar, IconEye, IconPlus, IconPointFilled, IconSearch } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import orders from './Orders.module.css';
import {
  filterGoodsReceipts,
  GRN_NO_FILTER,
  GRN_STATUSES,
  GRN_TONE,
  type GrnFilter,
} from './orders.js';
import classes from './Purchasing.module.css';
import { sortItems } from './requisitions.js';
import type { GoodsReceiptSource } from './source.js';
import {
  LoadError,
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from './table.js';
import type { GoodsReceiptStatus, GoodsReceiptSummary, GoodsReceiptList as List } from './types.js';

type Column = 'grnNo' | 'date' | 'poNo' | 'vessel' | 'receivedBy' | 'status';

const SORT_VALUE: Record<Column, (item: GoodsReceiptSummary) => string | number | null> = {
  grnNo: (item) => item.grnNo,
  date: (item) => item.receivedDate,
  poNo: (item) => item.poNo,
  vessel: (item) => item.vesselName,
  receivedBy: (item) => item.receivedByName,
  status: (item) => item.status,
};

const PAGE_SIZE = 10;

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; list: List; vessels: PersonnelVessel[] };

export function GoodsReceiptStatusBadge({ status }: { status: GoodsReceiptStatus }) {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={GRN_TONE[status]} icon={<IconPointFilled size={10} />}>
      {t(`purchasing.grn.statuses.${status}`)}
    </StatusBadge>
  );
}

/**
 * Mal kabulü listesi (`gemi-satin-alma.jpeg` 6 "Goods Receipt (GR)"): arama, Vessel, Status ve
 * tarih aralığı süzgeçleri; GR No / Date / PO No / Vessel / Received By / Status / Actions tablosu
 * ve sayfalama. Gemide "New GRN"; ofiste salt okunur (ADR 0026 §1; ADR 0040).
 */
export function GoodsReceiptList({
  source,
  onOpen,
  onNew,
}: {
  source: GoodsReceiptSource;
  onOpen: (id: string) => void;
  onNew: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.grn.list.${key}`, options);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [filter, setFilter] = useState<GrnFilter>(GRN_NO_FILTER);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState<Column>>({ key: 'grnNo', dir: 'desc' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const [list, vessels] = await Promise.all([source.list(), source.vessels()]);
      setState({ kind: 'ready', list, vessels });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state : null;
  const items = useMemo(
    () =>
      ready
        ? sortItems(filterGoodsReceipts(ready.list.items, filter), sort.dir, SORT_VALUE[sort.key])
        : [],
    [ready, filter, sort],
  );
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const change = (next: Partial<GrnFilter>) => {
    setFilter((f) => ({ ...f, ...next }));
    setPage(1);
  };
  const ship = source.scope === 'ship';
  // GRN gemide girilir (ADR 0026 §1): ofiste düğme yok
  const canCreate = Boolean(ready?.list.canCreate && source.editor);

  return (
    <div className={classes.screen}>
      <PageHeader title={k('title')} description={k(ship ? 'descriptionShip' : 'descriptionWeb')} />

      <Panel flush aria-label={k('table')}>
        <div className={classes.toolbar} data-single={canCreate ? undefined : true}>
          {canCreate ? (
            <Button leftSection={<IconPlus size={16} />} onClick={onNew}>
              {k('new')}
            </Button>
          ) : null}
          <form
            className={orders.filters}
            aria-label={k('filters')}
            onSubmit={(event) => event.preventDefault()}
          >
            <TextInput
              label={k('search')}
              placeholder={k('searchPlaceholder')}
              leftSection={<IconSearch size={16} aria-hidden />}
              value={filter.search}
              onChange={(event) => change({ search: event.currentTarget.value })}
            />
            <Select
              label={k('vessel')}
              data={[
                { value: '', label: k('allVessels') },
                ...(ready?.vessels ?? []).map((v) => ({ value: v.id, label: v.name })),
              ]}
              value={ship ? (ready?.vessels[0]?.id ?? '') : (filter.vesselId ?? '')}
              allowDeselect={false}
              readOnly={ship}
              {...(ship ? { rightSection: null } : {})}
              onChange={(value) => change({ vesselId: value || null })}
            />
            <Select
              label={k('status')}
              data={[
                { value: '', label: k('allStatuses') },
                ...GRN_STATUSES.map((value) => ({
                  value,
                  label: t(`purchasing.grn.statuses.${value}`),
                })),
              ]}
              value={filter.status ?? ''}
              allowDeselect={false}
              onChange={(value) => change({ status: (value || null) as GoodsReceiptStatus | null })}
            />
            <fieldset className={classes.range}>
              <legend className={classes.label}>{k('dateRange')}</legend>
              <div className={classes.rangeBox}>
                <IconCalendar size={16} aria-hidden />
                <DayInput
                  variant="unstyled"
                  size="xs"
                  rightSection={null}
                  aria-label={k('dateFrom')}
                  value={filter.from}
                  maxDate={filter.to ?? undefined}
                  onChange={(value) => change({ from: value })}
                />
                <span aria-hidden>–</span>
                <DayInput
                  variant="unstyled"
                  size="xs"
                  rightSection={null}
                  aria-label={k('dateTo')}
                  value={filter.to}
                  minDate={filter.from ?? undefined}
                  onChange={(value) => change({ to: value })}
                />
              </div>
            </fieldset>
          </form>
        </div>
        {state.kind === 'error' ? (
          <LoadError title={k('error')} code={state.code} onRetry={() => void load()} />
        ) : state.kind === 'loading' ? (
          <TableSkeleton columns={7} label={k('table')} />
        ) : (ready?.list.items.length ?? 0) === 0 ? (
          <EmptyState title={k('emptyTitle')} description={k(ship ? 'emptyShip' : 'emptyWeb')} />
        ) : items.length === 0 ? (
          <NoMatch
            title={k('noMatch')}
            onClear={() => {
              setFilter(GRN_NO_FILTER);
              setPage(1);
            }}
          />
        ) : (
          <>
            <section
              className={table.tableWrap}
              aria-label={k('table')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${classes.table}`}>
                <thead>
                  <tr>
                    {(Object.keys(SORT_VALUE) as Column[]).map((column) => (
                      <SortableTh
                        key={column}
                        column={column}
                        label={k(`columns.${column}`)}
                        sort={sort}
                        onSort={setSort}
                        {...(column === 'grnNo' ? { className: classes.key } : {})}
                      />
                    ))}
                    <th scope="col" data-numeric>
                      {t('purchasing.po.list.columns.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item) => (
                    <tr key={item.id}>
                      <td className={classes.key}>
                        <a
                          className={classes.link}
                          href={`?id=${item.id}`}
                          aria-label={k('open', { no: item.grnNo })}
                          onClick={(event) => {
                            event.preventDefault();
                            onOpen(item.id);
                          }}
                        >
                          {item.grnNo}
                        </a>
                      </td>
                      <td>{formatDay(item.receivedDate)}</td>
                      <td>{item.poNo}</td>
                      <td>{item.vesselName}</td>
                      <td>{item.receivedByName}</td>
                      <td>
                        <GoodsReceiptStatusBadge status={item.status} />
                      </td>
                      <td data-numeric>
                        <ActionIcon
                          variant="subtle"
                          aria-label={k('open', { no: item.grnNo })}
                          onClick={() => onOpen(item.id)}
                        >
                          <IconEye size={18} />
                        </ActionIcon>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, items.length),
                total: items.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
      </Panel>
    </div>
  );
}
